import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { UploadProvider } from './context/UploadContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ImpersonationLoader from './components/ImpersonationLoader';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyOTP from './pages/VerifyOTP';
import Verify2FA from './pages/Verify2FA';
import VerifyEmail from './pages/VerifyEmail';
import VerifyMobile from './pages/VerifyMobile';
import DashboardContent from './pages/DashboardContent';
import Users from './pages/Users';
import StyleGuide from './pages/StyleGuide';
import TestAuth from './pages/TestAuth';
import VerificationTest from './pages/VerificationTest';
import EmailTemplates from './pages/EmailTemplates';
import Profile from './pages/Profile';
import PricingPlans from './pages/PricingPlans';
import DataroomManagement from './pages/DataroomManagement';
import DataroomDetail from './pages/DataroomDetail';
import DataroomList from './pages/DataroomList';
import Features from './pages/Features';
import Subscriptions from './pages/Subscriptions';
import Roles from './pages/Roles';
import Permissions from './pages/Permissions';
import FileManager from './pages/FileManager';
import FileManagerLanding from './pages/FileManagerLanding';
import SystemConfig from './pages/SystemConfig';
import Tags from './pages/Tags';
import SuperAdminTags from './pages/SuperAdminTags';
import SecurityTest from './pages/SecurityTest';
import Invitations from './pages/Invitations';
import DataroomRoles from './pages/DataroomRoles';
import AllInvitations from './pages/AllInvitations';
import SuperAdminRoles from './pages/SuperAdminRoles';
import SuperAdminInvitations from './pages/SuperAdminInvitations';
import Members from './pages/Members';
import UserInvitations from './pages/UserInvitations';
import InvitationDetails from './pages/InvitationDetails';
import Notifications from './pages/Notifications';
import PredefineTemplates from './pages/PredefineTemplates';
import TestImportTemplate from './pages/TestImportTemplate';
import { useAuth } from './contexts/AuthContext';
import { getUserRole } from './utils/roleRedirect';
import NotificationPermission from './components/NotificationPermission';
import pusherService from './services/pusherService';
import notificationService from './services/notificationService';
import laravelEcho from './services/laravelEcho';
import './utils/debugNotification'; // Debug helper
import './utils/suppressWebSocketWarnings'; // Suppress WebSocket warnings

// Conditional Tags Component
const ConditionalTagsComponent = () => {
  const { user } = useAuth();
  const userRole = getUserRole(user);
  
  // Super admin gets the admin tags management view
  if (userRole === 'super-admin') {
    return <SuperAdminTags />;
  }
  
  // Regular users don't have a standalone tags page
  // They access tags within dataroom context
  return null;
};

// Initialize default theme
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('theme');
  const savedColorScheme = localStorage.getItem('colorScheme');
  
  // If no theme is saved, set black and white as default
  if (!savedTheme || !savedColorScheme) {
    const defaultTheme = 'black';
    const defaultColorScheme = 'white';
    
    localStorage.setItem('theme', defaultTheme);
    localStorage.setItem('colorScheme', defaultColorScheme);
    
    // Apply default blue theme with white color scheme
    const root = document.documentElement;

    // Blue theme with proper colors
    root.style.setProperty('--color-primary', '#5492F7'); // Blue for buttons
    root.style.setProperty('--color-secondary', '#1F2937');
    root.style.setProperty('--color-accent', '#111827');
    root.style.setProperty('--color-background', '#FFFFFF'); // White background
    root.style.setProperty('--color-surface', '#F9FAFB'); // Light surface
    root.style.setProperty('--color-text', '#111827'); // Dark text
    root.style.setProperty('--color-border', '#E5E7EB'); // Light borders

    // Blue button hover and sidebar text settings
    root.style.setProperty('--color-primary-hover', '#3671d9');
    root.style.setProperty('--sidebar-text', '#FFFFFF'); // White sidebar text
    root.style.setProperty('--sidebar-hover-bg', 'rgba(255, 255, 255, 0.1)');
    root.style.setProperty('--sidebar-active-bg', 'rgba(255, 255, 255, 0.15)');
  }
};

function App() {
  const [notificationsInitialized, setNotificationsInitialized] = useState(false);
  const [echoInitialized, setEchoInitialized] = useState(false);

  useEffect(() => {
    initializeTheme();
    
    // Initialize Laravel Echo first
    const initEcho = () => {
      try {
        const echo = laravelEcho.initialize();
        if (echo) {
          setEchoInitialized(true);
        }
      } catch (error) {
      }
    };
    
    // Initialize notifications when app loads
    const initNotifications = async () => {
      try {
        // Initialize notification service
        await notificationService.initialize();
        
        // Get user from auth context if available
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        if (user?.id) {
          // Initialize Pusher for authenticated users
          await pusherService.initialize(user.id);
        }
        
        setNotificationsInitialized(true);
      } catch (error) {
      }
    };
    
    // Initialize Echo first, then notifications
    initEcho();
    initNotifications();
    
    // Cleanup on unmount
    return () => {
      pusherService.disconnect();
      laravelEcho.disconnect();
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <UploadProvider>
            <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4ade80',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <ImpersonationLoader />
          {/* Add Notification Permission Component */}
          {notificationsInitialized && <NotificationPermission />}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-otp" element={<VerifyOTP />} />
            <Route path="/verify-2fa" element={<Verify2FA />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-mobile" element={<VerifyMobile />} />
            <Route path="/style-guide" element={<StyleGuide />} />
            <Route path="/test-auth" element={<TestAuth />} />
            <Route path="/verification-test" element={<VerificationTest />} />
            <Route path="/security-test" element={<SecurityTest />} />
            <Route element={<ProtectedRoute />}>
              {/* Common routes for all authenticated users */}
              <Route path="/dashboard" element={<DashboardContent />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/notifications" element={<Notifications />} />
              
              {/* User Invitations - for all users to see their pending invitations */}
              <Route path="/my-invitations" element={<UserInvitations />} />
              <Route path="/invitations/pending" element={<UserInvitations />} />
              <Route path="/invitation/:token" element={<InvitationDetails />} />
              
              {/* Super-admin only routes */}
              <Route path="/users" element={<RoleBasedRoute allowedRoles={['super-admin']}><Users /></RoleBasedRoute>} />
              <Route path="/email-templates" element={<RoleBasedRoute allowedRoles={['super-admin']}><EmailTemplates /></RoleBasedRoute>} />
              <Route path="/pricing-plans" element={<RoleBasedRoute allowedRoles={['super-admin']}><PricingPlans /></RoleBasedRoute>} />
              <Route path="/predefined-templates" element={<RoleBasedRoute allowedRoles={['super-admin']}><PredefineTemplates /></RoleBasedRoute>} />
              <Route path="/test-import-template" element={<TestImportTemplate />} />
              <Route path="/dataroom-management" element={<RoleBasedRoute allowedRoles={['super-admin']}><DataroomManagement /></RoleBasedRoute>} />
          <Route path="/dataroom-management/:uuid" element={<RoleBasedRoute allowedRoles={['super-admin']}><DataroomDetail /></RoleBasedRoute>} />
              <Route path="/features" element={<RoleBasedRoute allowedRoles={['super-admin']}><Features /></RoleBasedRoute>} />
              <Route path="/roles" element={<RoleBasedRoute allowedRoles={['super-admin']}><Roles /></RoleBasedRoute>} />
              <Route path="/permissions" element={<RoleBasedRoute allowedRoles={['super-admin']}><Permissions /></RoleBasedRoute>} />
              <Route path="/system-config" element={<RoleBasedRoute allowedRoles={['super-admin']}><SystemConfig /></RoleBasedRoute>} />
              
              {/* Super Admin Tags Management */}
              <Route path="/tags" element={
                <RoleBasedRoute allowedRoles={['super-admin']}>
                  <SuperAdminTags />
                </RoleBasedRoute>
              } />

              {/* Super Admin Invitations Management */}
              <Route path="/invitations" element={
                <RoleBasedRoute allowedRoles={['super-admin']}>
                  <SuperAdminInvitations />
                </RoleBasedRoute>
              } />

              {/* Super Admin All Invitations Management */}
              <Route path="/all-invitations" element={
                <RoleBasedRoute allowedRoles={['super-admin']}>
                  <AllInvitations />
                </RoleBasedRoute>
              } />
              
              {/* Super Admin Dataroom Roles Management */}
              <Route path="/admin/dataroom-roles" element={
                <RoleBasedRoute allowedRoles={['super-admin']}>
                  <SuperAdminRoles />
                </RoleBasedRoute>
              } />
              
              {/* User (admin role) routes */}
              <Route path="/subscriptions" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><Subscriptions /></RoleBasedRoute>} />
              <Route path="/datarooms" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><DataroomList /></RoleBasedRoute>} />
              <Route path="/file-manager" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManagerLanding /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/favorites" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager specialFilterProp="favorites" /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/recent-uploaded" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager specialFilterProp="recent_uploaded" /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/recent-viewed" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager specialFilterProp="recent_opened" /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/recycle-bin" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager isRecycleBin={true} /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/tags" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/invitations" element={<RoleBasedRoute allowedRoles={['super-admin', 'admin', 'user']}><FileManager /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/roles" element={<RoleBasedRoute allowedRoles={['super-admin', 'admin', 'user']}><FileManager /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/members" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/files/email-notifications" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/tags" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><Tags /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/invitations" element={<RoleBasedRoute allowedRoles={['super-admin', 'admin', 'user']}><Invitations /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/roles" element={<RoleBasedRoute allowedRoles={['super-admin', 'admin', 'user']}><DataroomRoles /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/members" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><Members /></RoleBasedRoute>} />
              <Route path="/datarooms/:dataroomUuid/email-notifications" element={<RoleBasedRoute allowedRoles={['admin', 'user']}><FileManager /></RoleBasedRoute>} />
            </Route>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </UploadProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;